import React, { useState } from "react";
import { Form, Button, FormControl, InputGroup, Card, Badge, Nav, Row, Col } from "react-bootstrap";
import { FaRobot, FaTrash, FaLink, FaFont, FaMagic, FaCloudUploadAlt, FaInfoCircle } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import { generateSourceWithAi } from "../../../../../features/ai_chatbots/aiChatbotsService";

interface AiAssistantSourceFormProps {
    chatbotId: number;
    initialTitle: string;
    initialLang: string;
    onGenerate: (content: string, title?: string, lang?: string) => void;
}

interface SourceItem {
    id: string;
    type: "file" | "url" | "text";
    value: string | File;
    name: string;
}

const AiAssistantSourceForm: React.FC<AiAssistantSourceFormProps> = ({
    chatbotId,
    initialTitle,
    initialLang,
    onGenerate
}) => {
    const [title, setTitle] = useState(initialTitle);
    const [lang, setLang] = useState(initialLang);
    const [instruction, setInstruction] = useState("");
    const [items, setItems] = useState<SourceItem[]>([]);

    // UI State
    const [inputType, setInputType] = useState<"url" | "file" | "text">("url");

    // Inputs
    const [tempUrl, setTempUrl] = useState("");
    const [tempText, setTempText] = useState("");
    const [tempFile, setTempFile] = useState<File | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [debugLog, setDebugLog] = useState<string | null>(null);

    const languages = [
        { code: "en", name: "English", flag: "🇬🇧" },
        { code: "tr", name: "Türkçe", flag: "🇹🇷" },
        { code: "de", name: "Deutsch", flag: "🇩🇪" },
        { code: "fr", name: "Français", flag: "🇫🇷" },
        { code: "es", name: "Español", flag: "🇪🇸" },
    ];

    const [generatedResult, setGeneratedResult] = useState<string | null>(null);

    const generateId = () => Math.random().toString(36).substr(2, 9);

    const addUrl = () => {
        if (!tempUrl.trim()) return;
        setItems([...items, { id: generateId(), type: "url", value: tempUrl.trim(), name: tempUrl }]);
        setTempUrl("");
    };

    const addText = () => {
        if (!tempText.trim()) return;
        setItems([...items, {
            id: generateId(),
            type: "text",
            value: tempText.trim(),
            name: tempText.substring(0, 30) + (tempText.length > 30 ? "..." : "")
        }]);
        setTempText("");
    };

    const addFile = (file: File) => {
        setItems([...items, { id: generateId(), type: "file", value: file, name: file.name }]);
        setTempFile(null);
    };

    const removeItem = (id: string) => {
        setItems(items.filter(i => i.id !== id));
    };

    const handleGenerate = async () => {
        if (!title.trim()) {
            setError("Source Title is required.");
            return;
        }
        if (items.length === 0 && !generatedResult) { // Allow validation pass if just editing result? No, regeneration requires inputs.
            setError("Please add at least one source (URL, File, or Text).");
            return;
        }

        setLoading(true);
        setError(null);
        setWarnings([]);
        setDebugLog(null);
        setGeneratedResult(null); // Clear previous result if regenerating

        const formData = new FormData();
        formData.append("chatbot_id", String(chatbotId));
        formData.append("title", title);
        formData.append("lang", lang);
        if (instruction.trim()) {
            formData.append("instruction", instruction.trim());
        }

        let combinedTextParts: string[] = [];
        let urlList: string[] = [];

        items.forEach(item => {
            if (item.type === "url") {
                urlList.push(item.value as string);
            } else if (item.type === "file") {
                formData.append("files", item.value as File);
            } else if (item.type === "text") {
                combinedTextParts.push(item.value as string);
            }
        });

        if (urlList.length > 0) {
            // Send as JSON string to ensure list is preserved and not overwritten
            const jsonUrls = JSON.stringify(urlList);
            console.log("DEBUG: Sending URLs as JSON:", jsonUrls);
            formData.append("urls", jsonUrls);
        }

        if (combinedTextParts.length > 0) {
            console.log("DEBUG: Sending Text Parts:", combinedTextParts.length);
            formData.append("text", combinedTextParts.join("\n\n---\n\n"));
        }

        try {
            const result = await generateSourceWithAi(chatbotId, formData);
            if (result && result.content) {
                setGeneratedResult(result.content);
                if (result.failed_sources && result.failed_sources.length > 0) {
                    setWarnings(result.failed_sources);
                }
                if (result.debug_log) {
                    setDebugLog(result.debug_log);
                }
            } else {
                setError("AI response was empty.");
            }
        } catch (err: any) {
            setError(err.message || "Failed to generate content.");
        } finally {
            setLoading(false);
        }
    };

    const handleApply = () => {
        if (generatedResult) {
            onGenerate(generatedResult, title, lang);
        }
    };

    return (
        <div className="ai-assistant-container p-3">
            {/* Header Section */}
            <div className="d-flex align-items-center mb-4 pb-3 border-bottom border-light">
                <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                    <Icon icon={FaRobot} className="text-primary" size="1.5rem" />
                </div>
                <div>
                    <h5 className="mb-1 fw-bold text-dark">AI Knowledge Assistant</h5>
                    <p className="mb-0 text-muted small">
                        Provide raw data (URLs, files, or text) and let AI structure it into a perfect knowledge source.
                    </p>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-center mb-4 border-0 shadow-sm">
                    <Icon icon={FaInfoCircle} className="me-2" />
                    {error}
                </div>
            )}



            {/* Main Form */}
            <Row className="g-4 mb-4">
                <Col md={8}>
                    <Form.Group>
                        <Form.Label className="small text-uppercase text-muted fw-bold">Source Title</Form.Label>
                        <Form.Control
                            type="text"
                            className="form-control-lg border-0 bg-light"
                            placeholder="e.g. Annual Report 2024"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label className="small text-uppercase text-muted fw-bold">Output Language</Form.Label>
                        <Form.Select
                            className="form-select-lg border-0 bg-light"
                            value={lang}
                            onChange={e => setLang(e.target.value)}
                        >
                            {languages.map(l => (
                                <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Input Sources Section */}
            <Card className="border-0 shadow-sm mb-4 overflow-hidden">
                <div className="bg-light p-2 border-bottom">
                    <Nav variant="pills" className="nav-justified gap-2" activeKey={inputType}>
                        <Nav.Item>
                            <Nav.Link
                                eventKey="url"
                                onClick={() => setInputType("url")}
                                className={inputType === "url" ? "bg-white text-primary shadow-sm" : "text-muted"}
                            >
                                <Icon icon={FaLink} className="me-2" /> Web URL
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                eventKey="file"
                                onClick={() => setInputType("file")}
                                className={inputType === "file" ? "bg-white text-primary shadow-sm" : "text-muted"}
                            >
                                <Icon icon={FaCloudUploadAlt} className="me-2" /> Upload File
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                eventKey="text"
                                onClick={() => setInputType("text")}
                                className={inputType === "text" ? "bg-white text-primary shadow-sm" : "text-muted"}
                            >
                                <Icon icon={FaFont} className="me-2" /> Raw Text
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>

                <Card.Body className="bg-white p-4">
                    {inputType === "url" && (
                        <div className="fade-in">
                            <InputGroup>
                                <FormControl
                                    placeholder="https://example.com/page"
                                    value={tempUrl}
                                    className="border-end-0"
                                    onChange={e => setTempUrl(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && addUrl()}
                                />
                                <Button variant="action-ai" onClick={addUrl} disabled={!tempUrl.trim()}>
                                    <Icon icon={FaLink} className="me-1" /> Add
                                </Button>
                            </InputGroup>
                            <Form.Text className="text-muted">Requires a public URL. The AI will read the main page content.</Form.Text>
                        </div>
                    )}

                    {inputType === "file" && (
                        <div className="fade-in text-center p-4 border rounded-3 border-dashed bg-light">
                            <input
                                type="file"
                                id="ai-file-upload-hidden"
                                className="d-none"
                                onChange={(e: any) => e.target.files?.[0] && addFile(e.target.files[0])}
                                onClick={(e: any) => e.target.value = null}
                            />
                            <label htmlFor="ai-file-upload-hidden" className="btn btn-action-ai mb-2">
                                <Icon icon={FaCloudUploadAlt} className="me-2" /> Choose Document
                            </label>
                            <div className="small text-muted">Supports PDF, TXT, MD, CSV, JSON</div>
                        </div>
                    )}

                    {inputType === "text" && (
                        <div className="fade-in">
                            <FormControl
                                as="textarea"
                                placeholder="Paste your notes, snippets or raw data here..."
                                rows={3}
                                className="mb-2"
                                value={tempText}
                                onChange={e => setTempText(e.target.value)}
                            />
                            <div className="d-flex justify-content-end">
                                <Button size="sm" variant="action-ai" onClick={addText} disabled={!tempText.trim()}>
                                    Add Text Snippet
                                </Button>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Added Items List */}
            {items.length > 0 && (
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="small text-uppercase text-muted fw-bold">Sources to Process</label>
                        <Badge bg="secondary" pill>{items.length}</Badge>
                    </div>
                    <div className="list-group list-group-flush rounded-3 border overflow-hidden">
                        {items.map(item => (
                            <div key={item.id} className="list-group-item d-flex align-items-center justify-content-between p-3 bg-white">
                                <div className="d-flex align-items-center overflow-hidden">
                                    <div className={`p-2 rounded-circle me-3 flex-shrink-0 ${item.type === 'url' ? 'bg-info bg-opacity-10 text-info' :
                                        item.type === 'file' ? 'bg-warning bg-opacity-10 text-warning' :
                                            'bg-success bg-opacity-10 text-success'
                                        }`}>
                                        <Icon icon={item.type === 'url' ? FaLink : item.type === 'file' ? FaCloudUploadAlt : FaFont} />
                                    </div>
                                    <div className="text-truncate">
                                        <div className="fw-medium text-dark text-truncate">{item.name}</div>
                                        <div className="small text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{item.type}</div>
                                    </div>
                                </div>
                                <Button variant="link" className="text-danger p-2" onClick={() => removeItem(item.id)}>
                                    <Icon icon={FaTrash} />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* AI Instruction */}
            <Form.Group className="mb-4">
                <Form.Label className="small text-uppercase text-muted fw-bold">Instructions for AI (Optional)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    className="bg-light border-0"
                    placeholder="e.g. Summarize the key points, focus on technical details, format as valid Markdown..."
                    value={instruction}
                    onChange={e => setInstruction(e.target.value)}
                />
            </Form.Group>

            {/* Warnings Alert (Moved here) */}
            {warnings.length > 0 && (
                <div className="alert alert-warning border-0 shadow-sm mb-4">
                    <div className="d-flex align-items-center mb-2 fw-bold text-warning-dark">
                        <Icon icon={FaInfoCircle} className="me-2" />
                        Some sources could not be processed:
                    </div>
                    <ul className="mb-0 small ps-3">
                        {warnings.map((w, idx) => (
                            <li key={idx}>{w}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Generated Result Preview */}
            {generatedResult && (
                <div className="mb-4 fade-in-up">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="small text-uppercase text-success fw-bold">
                            <Icon icon={FaMagic} className="me-1" /> AI Generated Content
                        </label>
                        <Badge bg="success">Ready</Badge>
                    </div>
                    <Form.Control
                        as="textarea"
                        rows={10}
                        className="mb-3 border-success border-opacity-25 bg-success bg-opacity-10"
                        value={generatedResult}
                        onChange={e => setGeneratedResult(e.target.value)}
                    />



                    {loading && (
                        <div className="d-flex justify-content-center mb-3 animate-pulse">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <div className="d-flex gap-3">
                        <Button
                            size="lg"
                            className="flex-grow-1 fw-bold shadow-sm bg-primary text-white border-0"
                            onClick={handleApply}
                        >
                            <Icon icon={FaMagic} className="me-2" /> Use This Content
                        </Button>

                        {/* Action Button (Generate/Regenerate) */}
                        <Button
                            size="sm"
                            onClick={handleGenerate}
                            disabled={loading || items.length === 0}
                            className={`flex-grow-1 fw-bold shadow-sm bg-info text-white border-0`}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Regenerating...
                                </>
                            ) : (
                                <>
                                    <Icon icon={generatedResult ? FaMagic : FaMagic} className="me-2" />
                                    Regenerate Content
                                </>
                            )}
                        </Button>
                    </div>

                    {loading && (
                        <div className="text-center mt-3 text-muted small animate-pulse">
                            Analyzing content and writing comprehensive source...
                        </div>
                    )}

                    <div className="text-center mt-3 small text-muted">
                        Not satisfied? Adjust inputs above and click Generate again.
                    </div>
                </div>
            )}


            {/* Action Button (Initial Generate) */}
            {
                !generatedResult && (
                    <>
                        {loading && (
                            <div className="d-flex justify-content-center mb-3 animate-pulse">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        <Button
                            variant="premium-ai"
                            size="lg"
                            onClick={handleGenerate}
                            disabled={loading || items.length === 0}
                            className="w-100 fw-bold rounded-3 shadow-sm py-3"
                        >
                            {loading ? "Generating Knowledge..." : (
                                <>
                                    <Icon icon={FaMagic} className="me-2" />
                                    Generate Structured Source
                                </>
                            )}
                        </Button>
                    </>
                )
            }


        </div >
    );
};

export default AiAssistantSourceForm;
